import Style from 'ol/style/Style';
import { VectorMath } from 'shared/common/domain/vectorMath';
import { Color } from 'shared/common/domain/color';
import { parseRichText } from '../../mapStyles/domain/richText';
import type { PlaceBackgroundPattern, PlaceTextAlignment, PlaceTextPosition } from '../../mapStyles/domain/placeContext';

const hexColorPattern = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function safeAlpha(color: string, opacity: number, fallback: string): string {
  const value = hexColorPattern.test(color) ? color : fallback;
  try {
    return Color.setAlpha(value, opacity);
  } catch {
    return Color.setAlpha(fallback, opacity);
  }
}

// Pattern canvases and parsed labels are reused across features/frames; both are
// pure functions of their inputs and were previously rebuilt on every render.
const patternCanvasCache = new Map<string, HTMLCanvasElement | null>();
const labelTextCache = new Map<string, string>();

function getPatternCanvas(pattern: PlaceBackgroundPattern, color: string): HTMLCanvasElement | null {
  const key = `${pattern}|${color}`;
  const cached = patternCanvasCache.get(key);
  if (cached !== undefined) return cached;
  const canvas = createPatternCanvas(pattern, color);
  patternCanvasCache.set(key, canvas);
  return canvas;
}

function getLabelText(label: string): string {
  const cached = labelTextCache.get(label);
  if (cached !== undefined) return cached;
  const richText = parseRichText(label);
  const text = richText.tokens.map((token) => (token.type === 'text' ? token.content : '')).join('');
  labelTextCache.set(label, text);
  return text;
}

function createPatternCanvas(
  pattern: PlaceBackgroundPattern,
  color: string,
): HTMLCanvasElement | null {
  if (pattern === 'solid') return null;

  const size = 8;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1;

  switch (pattern) {
    case 'hatch':
      ctx.beginPath();
      ctx.moveTo(0, size);
      ctx.lineTo(size, 0);
      ctx.stroke();
      break;
    case 'cross':
      ctx.beginPath();
      ctx.moveTo(0, size);
      ctx.lineTo(size, 0);
      ctx.moveTo(0, 0);
      ctx.lineTo(size, size);
      ctx.stroke();
      break;
    case 'dots':
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, 1.2, 0, Math.PI * 2);
      ctx.fill();
      break;
  }

  return canvas;
}

export const placeStyle = new Style({
  renderer: (coordinates, state) => {
    const ring = (coordinates as number[][][])[0];
    if (!ring || ring.length < 4) return;

    const p0 = ring[0]!;
    const p1 = ring[1]!;
    const p2 = ring[2]!;
    const p3 = ring[3]!;

    const properties = state.feature.getProperties();

    const backgroundColor = properties['backgroundColor'] as string;
    const backgroundOpacity = (properties['backgroundOpacity'] as number) ?? 1;
    const backgroundPattern = (properties['backgroundPattern'] as PlaceBackgroundPattern) ?? 'solid';
    const outlineColor = properties['outlineColor'] as string;
    const outlineSize = (properties['outlineSize'] as number) ?? 1;
    const accessibleOutlineColor = (properties['accessibleOutlineColor'] as string) ?? outlineColor;
    const inaccessibleOutlineColor = (properties['inaccessibleOutlineColor'] as string) ?? outlineColor;
    const accessFront = properties['accessFront'] as boolean | undefined;
    const accessRight = properties['accessRight'] as boolean | undefined;
    const accessBack = properties['accessBack'] as boolean | undefined;
    const accessLeft = properties['accessLeft'] as boolean | undefined;
    const label = properties['label'] as string;
    const textColor = properties['textColor'] as string;
    const textOpacity = (properties['textOpacity'] as number) ?? 1;
    const textSize = (properties['textSize'] as number) ?? 12;
    const textAlignment = (properties['textAlignment'] as PlaceTextAlignment) ?? 'center';
    const textPosition = (properties['textPosition'] as PlaceTextPosition) ?? 'center';

    const ctx = state.context;

    ctx.save();

    ctx.beginPath();
    ctx.moveTo(p0[0]!, p0[1]!);
    ctx.lineTo(p1[0]!, p1[1]!);
    ctx.lineTo(p2[0]!, p2[1]!);
    ctx.lineTo(p3[0]!, p3[1]!);
    ctx.closePath();

    // Background
    const patternCanvas = getPatternCanvas(
      backgroundPattern,
      safeAlpha(backgroundColor, backgroundOpacity, '#ffffff'),
    );

    if (patternCanvas) {
      const canvasPattern = ctx.createPattern(patternCanvas, 'repeat');
      if (canvasPattern) {
        ctx.fillStyle = canvasPattern;
      } else {
        ctx.fillStyle = safeAlpha(backgroundColor, backgroundOpacity, '#ffffff');
      }
    } else {
      ctx.fillStyle = safeAlpha(backgroundColor, backgroundOpacity, '#ffffff');
    }
    ctx.fill();

    // Outline, drawn per edge so accessible and inaccessible sides can use
    // different colors. Edge order follows the place ring (front, right, back,
    // left); see placeGeometry corner layout.
    if (outlineSize > 0) {
      ctx.lineWidth = outlineSize;
      const edges: [number[], number[], boolean | undefined][] = [
        [p0, p1, accessFront],
        [p1, p2, accessRight],
        [p2, p3, accessBack],
        [p3, p0, accessLeft],
      ];
      for (const [start, end, accessible] of edges) {
        ctx.beginPath();
        ctx.moveTo(start[0]!, start[1]!);
        ctx.lineTo(end[0]!, end[1]!);
        ctx.strokeStyle = safeAlpha(accessible ? accessibleOutlineColor : inaccessibleOutlineColor, 1, '#000000');
        ctx.stroke();
      }
    }

    ctx.restore();

    // Text
    if (label) {
      const widthVector = VectorMath.subtract(p1, p0);
      const heightVector = VectorMath.subtract(p3, p0);
      const center = VectorMath.add(
        VectorMath.add(p0, VectorMath.multiply(widthVector, 0.5)),
        VectorMath.multiply(heightVector, 0.5),
      );

      // Direction the text baseline runs. Defaults to the width side; packages
      // opt into running the label along the length side via `labelAlongLength`.
      const labelAlongLength = properties['labelAlongLength'] as boolean | undefined;
      const textVector = labelAlongLength ? heightVector : widthVector;
      const crossVector = labelAlongLength ? widthVector : heightVector;

      const unitText = VectorMath.unit(textVector);
      let rotation = Math.atan2(unitText[1]!, unitText[0]!);

      const cameraVector = VectorMath.rotate([1, 0], -state.rotation);
      const upsideDown = VectorMath.dot(cameraVector, unitText) < 0;
      if (upsideDown) {
        rotation += Math.PI;
      }

      const scaledFontSize = Math.max(textSize / state.resolution, 5);

      let anchor = center;
      if (textPosition === 'top') {
        anchor = VectorMath.add(center, VectorMath.multiply(crossVector, upsideDown ? 0.35 : -0.35));
      } else if (textPosition === 'bottom') {
        anchor = VectorMath.add(center, VectorMath.multiply(crossVector, upsideDown ? -0.35 : 0.35));
      }

      const halfTextLength = VectorMath.length(textVector) / 2;
      if (textAlignment === 'left') {
        anchor = VectorMath.add(anchor, VectorMath.multiply(unitText, upsideDown ? halfTextLength * 0.9 : -halfTextLength * 0.9));
      } else if (textAlignment === 'right') {
        anchor = VectorMath.add(anchor, VectorMath.multiply(unitText, upsideDown ? -halfTextLength * 0.9 : halfTextLength * 0.9));
      }

      ctx.save();
      ctx.translate(anchor[0]!, anchor[1]!);
      ctx.rotate(rotation);
      ctx.font = `${scaledFontSize}px sans-serif`;
      ctx.textAlign = textAlignment === 'center' ? 'center' : textAlignment;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = safeAlpha(textColor, textOpacity, '#000000');

      const text = getLabelText(label);

      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  },
});
